import type { ReactNode, ThHTMLAttributes } from 'react';
import React, {
  cloneElement,
  type CSSProperties,
  forwardRef,
  type HTMLAttributes,
  isValidElement,
  type ReactElement,
  useId,
} from 'react';
import clsx from 'clsx';

import { TooltipInfo } from '../Tooltip';

import styles from './styles/Table.module.scss';

export type TableColumnType = {
  alignItems?: CSSProperties['alignItems'];
  highlight?: boolean;
  justifyContent?: CSSProperties['justifyContent'];
  mobileText?: ReactNode;
  mobileView?: 'hide';
  showDividers?: boolean;
  toolTipSide?: 'left' | 'right' | 'top' | 'bottom';
  tooltipText?: string;
} & ThHTMLAttributes<HTMLTableCellElement>;

export type TableCellType = {
  alignItems?: CSSProperties['alignItems'];
  highlight?: string;
  justifyContent?: CSSProperties['justifyContent'];
  showDividers?: boolean;
} & ThHTMLAttributes<HTMLTableCellElement>;

export type TableHeadType = {
  row?: number;
} & ThHTMLAttributes<HTMLTableSectionElement>;

export type TableBodyType = ThHTMLAttributes<HTMLTableSectionElement>;
export type TableFooterType = ThHTMLAttributes<HTMLTableSectionElement>;
export type TableRowType = ThHTMLAttributes<HTMLTableRowElement>;
export type TableCaptionType = ThHTMLAttributes<HTMLTableCaptionElement>;
export type TableType = HTMLAttributes<HTMLTableElement>;
/**
 * `GridTable` renders the wrapper that consumes both of these — `label` as its `aria-label`, and
 * `mobileColumns` as the `--mobileColumns` custom property its stylesheet reads. Plain `Table` has
 * neither: no `--mobileColumns` in `Table.module.scss`, and no element to hang the label on. They
 * used to live on the shared `TableType`, so `<Table mobileColumns={2}>` type-checked and did
 * nothing, and `label` reached the DOM as an invalid `<table label="...">` attribute. Keeping them
 * here means the compiler rejects both.
 */
export type GridTableType = {
  label?: string;
  mobileColumns?: number;
} & TableType;
export type TableGroupType = {
  label?: string | ReactElement;
} & HTMLAttributes<HTMLDivElement>;

export const TableHead = ({ children, className, row, ...props }: TableHeadType) => (
  <thead className={clsx(styles.tableHeader, className)} style={{ '--gridRow': row }} {...props}>
    {children}
  </thead>
);

export const TableBody = ({ children, className, ...props }: TableBodyType) => (
  <tbody className={clsx(styles.tableBody, className)} {...props}>
    {children}
  </tbody>
);

export const TableFooter = ({ children, className, ...props }: TableFooterType) => (
  <tfoot className={clsx(styles.tableFooter, className)} {...props}>
    {children}
  </tfoot>
);

export const TableRow = ({ children, className, size = 'm', ...props }: { size?: 's' | 'm' } & TableRowType) => (
  <tr className={clsx(styles.tableRow, styles[`tableRow-size--${size}`], className)} {...props}>
    {children}
  </tr>
);

export const TableCaption = ({ children, className, ...props }: TableCaptionType) => (
  <caption className={clsx(styles.tableCaption, className)} {...props}>
    {children}
  </caption>
);

export const TableColumn = ({
  alignItems = 'center',
  children,
  className,
  highlight,
  justifyContent = 'flex-start',
  mobileText,
  mobileView,
  showDividers,
  toolTipSide,
  tooltipText,
  ...props
}: TableColumnType) => (
  <th
    className={clsx(
      styles.tableColumn,
      styles[`mobile-${mobileView}`],
      {
        [styles.highlight]: highlight,
        [styles.showDividers]: showDividers,
      },
      className,
    )}
    {...props}
  >
    <div
      className={styles.columnWrapper}
      style={{
        '--alignItems': alignItems,
        '--justifyContent': justifyContent,
      }}
    >
      {mobileText ? (
        <>
          <span className={styles['headingText--mobile']}>{mobileText}</span>
          <span className={styles.headingText}>{children}</span>
        </>
      ) : (
        <>{children}</>
      )}
      {tooltipText && <TooltipInfo side={toolTipSide}>{tooltipText}</TooltipInfo>}
    </div>
  </th>
);

export const TableCell = ({
  alignItems = 'center',
  children,
  className,
  highlight,
  justifyContent = 'flex-start',
  showDividers,
  style,
  ...props
}: TableCellType) => {
  return (
    <td
      className={clsx(
        styles.tableCell,
        {
          [styles.highlight]: highlight,
          [styles[`highlight--${highlight}`]]: highlight,
          [styles.showDividers]: showDividers,
        },
        className,
      )}
      style={{
        ...style,
        '--alignItems': justifyContent,
        '--justifyContent': alignItems,
      }}
      {...props}
    >
      <div className={styles.cellWrapper}>{children}</div>
    </td>
  );
};

export const Table = forwardRef<HTMLTableElement, TableType>(({ children, className, ...props }, ref) => {
  return (
    <div className={styles.tableWrapper} data-cxy-name="table">
      <table className={clsx(styles.table, className)} ref={ref} {...props}>
        {children}
      </table>
    </div>
  );
});

export const TableGroup = ({ children, className, label }: TableGroupType) => {
  const headingId = useId();
  const elements = React.Children.map(children, child =>
    isValidElement<HTMLAttributes<HTMLTableElement>>(child)
      ? cloneElement<HTMLAttributes<HTMLTableElement>>(child, {
          'aria-labelledby': headingId,
        })
      : child,
  );

  return (
    <div className={clsx(styles.tableGroup, className)}>
      {label && (
        <h3 className={styles.tableLabel} id={headingId}>
          {label}
        </h3>
      )}
      {elements}
    </div>
  );
};
